import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { WorkspaceProjectElementNode } from 'api/graphQL/reactiveVars/element'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import { taskContentItemIds } from 'api/graphQL/reactiveVars/workspaceTasks'
import { showBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useReorderItems } from 'components/WorkspaceEditor/hooks/useReorderItems'
import { useSetSelectedKey } from 'components/WorkspaceEditor/NodeKeySelectionStore'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles/useTreeViewStyles'
import type { DataNode } from 'components/WorkspaceEditor/types/types'
import {
    getImportTypeInfo,
    isEditableElement,
    isManufacturerOrBODPill,
} from 'components/WorkspaceEditor/utils'
import {
    handleViewProductsWithElementItem,
    ProjectElementNodeExtended,
} from 'components/WorkspaceEditor/utils/productLibraryUtils'
import { useSelectedProjectElementNode } from 'components/WorkspaceEditor/Workspace2Store'
import { OM_DEACTIVATED_NODE_TOOLTIP } from 'pages/components/constants'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import TaskIndicatorButton from 'components/WorkspaceEditor/components/TaskIndicatorButton'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { isOMBestPracticeFeatureEnabled } from 'shared/utils/bestPracticeUtils'
import { getDialogUrl } from 'shared/utils/workspaceUtils'
import { BestPracticesIconWrapper } from '../../features/bestPractices/components/BestPracticesIconWrapper'
import { BP_NODE_KEYS } from '../../features/bestPractices/constants'
import { useBestPracticesMenu } from '../../features/bestPractices/hooks'
import { useApplyBPFromConfig } from '../../features/bestPractices/hooks/useApplyBPFromConfig'
import { useShouldRenderBPIcon } from '../../features/bestPractices/hooks/useShouldRenderBPIcon'
import { useBestPracticeManagementStore } from '../../features/bestPractices/store/BestPracticeManagementStore'
import {
    AddManufacturerButton,
    CustomManufacturerFieldsWrapper,
} from '../../features/customManufacturer'
import { LevelOfSpecificationIcon } from '../../features/levelOfSpecification/components/LevelOfSpecificationIcon'
import { usePromoteDemoteSelector } from '../../features/promoteDemote'
import {
    NodeContentRendererProps,
    NodeOrderProps,
    SpecifierNotesProps,
    TableContentProps,
} from '../NodeContents'
import { withLazyLoad } from '../withLazyComponent'
import { BestPracticesSettingPopover } from './BestPracticesSettingPopover'
import { ProductLibraryButton } from './ProductLibraryButton'
import { TreeViewHoveredIcons } from './TreeViewHoveredIcons'

// Block the browser's default word-selection on the second click of a
// double-click — single-click selection is preserved (e.detail === 1).
const preventDoubleClickWordSelect = (e: React.MouseEvent) => {
    if (e.detail > 1) e.preventDefault()
}

/**
 * Props for rendering node Title of <TreeView /> component
 */
export interface NodeTitleProps {
    /** The node to render */
    node: DataNode
    /** A flag whether the node is expanded */
    isExpanded: boolean
    /** A flag whether to show node order label */
    showNodeOrder: boolean
    /** The text value to render node order label */
    orderLabel: string
    /** Classes to style the components */
    classes: ReturnType<typeof useTreeViewStyles>
    /** a flag to determine whether to show specifier nodes or not */
    areNotesVisible?: boolean
    /** Optional tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
}

const SpecifierNotes = withLazyLoad<SpecifierNotesProps>(() =>
    import('../NodeContents').then((module) => ({
        default: module.SpecifierNotes,
    }))
)

const NodeOrder = withLazyLoad<NodeOrderProps>(() =>
    import('../NodeContents').then((module) => ({
        default: module.NodeOrder,
    }))
)

const ContentRenderer = withLazyLoad<NodeContentRendererProps>(() =>
    import('../NodeContents').then((module) => ({
        default: module.NodeContentRenderer,
    }))
)

const TableContent = withLazyLoad<TableContentProps>(() =>
    import('../NodeContents').then((module) => ({
        default: module.TableContent,
    }))
)

/**
 * Renders node title with specifier notes, order label, and content
 * @param {NodeTitleProps} props - Props for rendering node title
 * @returns {ReactElement} Styled node title
 */
export const NodeTitle = memo<NodeTitleProps>(
    ({
        node,
        isExpanded,
        showNodeOrder,
        orderLabel,
        classes,
        areNotesVisible,
        treeRef,
    }) => {
        const {
            type,
            contentItemNodes,
            isHidden,
            isUserAdded,
            isProductGroup,
            isProductType,
            isProductTypeContent,
        } = node
        const {
            displayWrap,
            inactiveText,
            nodeOrderPadding,
            articleTypography,
            paragraphTypography,
            customManufacturerNode,
            addManufacturerButtonNode,
            productLibraryButtonContainer,
            nonArticleNodesBPSpacing,
            promoteDemoteInFlight,
        } = classes
        const isContentTeamUser = isReactiveContentTeamFromStudioPage()?.value
        const isArticleNode = type === NODE_TYPES.Article
        const hasSpecialContentTypes = contentItemNodes.some(
            (node) =>
                node.type === NODE_TYPES.UnitsOfMeasure ||
                node.type === NODE_TYPES.ReplacementOption ||
                node.type === NODE_TYPES.OptionGroup
        )

        const { currentUserInfo } = useReactiveVar(userInfo)
        const { kpUserId: userId } = currentUserInfo
        const { isCurrentlyOnOMWorkspace, isOMUser } = useOMWorkspace()
        const { previewFeatures } = usePreviewFeatures()
        const selectedProjectElementNode = useSelectedProjectElementNode()
        const isPTNotAdded = selectedProjectElementNode?.isInProject === false

        const shouldSuppressStrikethrough =
            !isCurrentlyOnOMWorkspace && isPTNotAdded
        // In OM workspace, deactivated nodes have isHidden: false but isDeactivated: true
        // (the mutation sends isHidden: false + isContentDeactivatedInOM: true to the server)
        const isDeactivatedInOM =
            isCurrentlyOnOMWorkspace && Boolean(node.isDeactivated)
        const isVisuallyDeactivated = Boolean(isHidden) || isDeactivatedInOM
        const userSeesStrikethrough =
            !isContentTeamUser || isCurrentlyOnOMWorkspace
        const isContentHidden =
            isVisuallyDeactivated &&
            userSeesStrikethrough &&
            !shouldSuppressStrikethrough

        const specifierNoteClasses = isContentHidden ? 'disabled' : ''
        const showArticleNote = isExpanded && isArticleNode
        const showBPSetting = useReactiveVar(showBestPracticeSetting)
        const isPreviewNode = useBestPracticeManagementStore(
            (s) => s.previewSnapshot?.contentItemId === String(node.key)
        )
        const taskItemIds = useReactiveVar(taskContentItemIds)
        // node.key = contentItemView.id, but notes created from OM workspace store
        // dtvContentItemId as the contentItemId. Check both so indicators work in all workspaces.
        const hasTaskIndicator =
            previewFeatures.hasProjectTasks &&
            (taskItemIds.has(String(node.key)) ||
                !!(
                    node.contentItemView?.dtvContentItemId &&
                    taskItemIds.has(node.contentItemView.dtvContentItemId)
                ))
        const isBPManagementEnabled =
            isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace
        const applyBPFromConfig = useApplyBPFromConfig()
        const spanRef = useRef<HTMLSpanElement>(null)
        const { onKeyDown } = useReorderItems()
        const setSelectedKey = useSetSelectedKey()
        const { isPromoteDemoteInFlight } = usePromoteDemoteSelector((s) => ({
            isPromoteDemoteInFlight: s.isPromoteDemoteInFlight,
        }))
        const { elementItemValue } = useElementItemShallowSelector((state) => ({
            elementItemValue: state.elementItemValue,
        }))
        const { projectId } = useParams<{
            projectId: string
        }>()

        const handleTaskIndicatorClick = React.useCallback(() => {
            if (!projectId || !selectedProjectElementNode) return

            let family = ''
            let productType = ''
            const elType = selectedProjectElementNode.elementType

            if (elType === 'PRODUCTFAMILY') {
                family = selectedProjectElementNode.text?.split('|').join(' - ') ?? ''
            } else if (elType === 'PRODUCTTYPE') {
                const treeSegments = (selectedProjectElementNode.treePath ?? '').split('||').filter(Boolean)
                const parentSegment = treeSegments[treeSegments.length - 2] ?? ''
                family = parentSegment.split('|').filter(Boolean).join(' - ')
                productType = selectedProjectElementNode.text?.split('|').join(' - ') ?? ''
            }

            const familyId = elType === 'PRODUCTFAMILY'
                ? selectedProjectElementNode.elementId || ''
                : elType === 'PRODUCTTYPE'
                    ? elementItemValue.parentFamilyId || ''
                    : ''

            const contentItemId = node.contentItemView?.dtvContentItemId || String(node.key)

            const baseUrl = getDialogUrl(
                {
                    projectId,
                    elementNode: selectedProjectElementNode as ProjectElementNode,
                    family,
                    productType,
                    familyId: familyId || undefined,
                },
                'projecttasks'
            )

            const separator = baseUrl.includes('?') ? '&' : '?'
            const url = `${baseUrl}${separator}contentItemId=${encodeURIComponent(contentItemId)}`

            window.open(
                url,
                `ProjectTasks${projectId}`,
                `directories=no,titlebar=no,location=no,toolbar=no,menubar=no,resizable=yes,height=${window.screen.height},width=${window.screen.width}`
            )
        }, [projectId, selectedProjectElementNode, elementItemValue, node])

        // Best Practices menu management
        const {
            isBPMenuOpen,
            bpAnchorElement,
            openBPMenu,
            closeBPMenu,
        } = useBestPracticesMenu()

        const isProductRelatedContent =
            isContentTeamUser &&
            (isProductGroup || isProductType || isProductTypeContent)

        // In OM workspace with hasOMPTAutomation, PT content is only blocked when selected
        // element is a ProductFamily. Otherwise PT content and descendants are editable.
        // Remove the hasOMPTAutomation check when feature is production ready.
        const isOMPTContext =
            previewFeatures.hasOMPTAutomation && isCurrentlyOnOMWorkspace

        // Show 'Office Master Deactivation' tooltip on hover in AE workspace for deactivated nodes.
        // Remove the hasOMPTAutomation check when the feature is production ready.
        const isOMDeactivatedTooltipVisible =
            previewFeatures.hasOMPTAutomation &&
            !isContentTeamUser &&
            node.isDeactivated
        const isProductFamilySelected =
            selectedProjectElementNode?.elementType ===
            ProjectElementType.PRODUCTFAMILY
        const isProductTypeContentBlocked =
            isProductGroup ||
            ((!isOMPTContext || isProductFamilySelected) &&
                isProductTypeContent)
        const isNodeEditable =
            // PBI 2407205: Allow Product Type content to be editable
            // PT keys will be blocked at the PlainText component level
            // product group contents should not be editable on SMM/OM
            // Bug 2625302: user-added content under PT Groups must remain editable
            // Custom manufacturer pills are user-created but never flagged isUserAdded
            // (createOptimisticPillNode sets isUserAdded: false), so they need the
            // same exemption to stay editable under PT content (matches the
            // isUserAddedCustomMfr exemption in NodeContentRenderer, Bug 2684621).
            !(
                isContentTeamUser &&
                isProductTypeContentBlocked &&
                !isUserAdded &&
                !node.isCustomManufacturer
            ) &&
            // should be added to project
            selectedProjectElementNode?.isInProject &&
            // OM Workspace: Allow editing of inactive content (both articles and paragraphs)
            (isCurrentlyOnOMWorkspace ||
                // SMM/OM content
                isContentTeamUser ||
                // user added article
                (isArticleNode && isUserAdded) ||
                // anything other than article
                type !== NODE_TYPES.Article) &&
            // block editing while a promote/demote mutation is in flight
            !isPromoteDemoteInFlight

        const isProductLibraryButtonNode = String(node.key).startsWith(
            'product-library-btn'
        )

        const isOMAddManufacturerNode = String(node.key).startsWith(
            'om-add-mfr-btn'
        )

        const renderSpecifierNotes = (
            dataTestId = 'specifier-notes',
            isArticleNoteSpecifier = false
        ) =>
            !areNotesVisible ? (
                <></>
            ) : (
                <SpecifierNotes
                    node={node}
                    editable={isContentTeamUser}
                    isArticleNoteSpecifier={isArticleNoteSpecifier}
                    classes={specifierNoteClasses}
                    dataTestId={dataTestId}
                />
            )
        const isImportedFamily = useMemo(() => {
            const info = getImportTypeInfo(selectedProjectElementNode)
            return info.isOMImport || info.isProjectImport
        }, [selectedProjectElementNode])

        const showBestPracticesFeatureInNewWorkspace: boolean = useMemo(
            () =>
                processEnv.REACT_APP_ShowBestPracticesFeatureInNewWorkspace ===
                '1',
            [processEnv]
        )
        const showCustomManufacturer = useMemo(
            () =>
                node.isCustomManufacturer &&
                !isCurrentlyOnOMWorkspace &&
                !node.contentItemView?.isAddedInOfficeMasterDraftProject,
            [node, isCurrentlyOnOMWorkspace]
        )
        const shouldDisableBPMenuForUserAddedContent: boolean = useMemo(
            () =>
                processEnv.REACT_APP_EnableSmartProjectCopy !== '1' &&
                (isUserAdded ?? false),
            [processEnv, isUserAdded]
        )

        const isFromPPLTemplate =
            (selectedProjectElementNode as WorkspaceProjectElementNode)
                ?.isFromPPLTemplate ?? false

        const hasParentComments = useMemo(() => {
            return (
                (node.parentComments?.length ?? 0) > 0 &&
                node.type === NODE_TYPES.Article
            )
        }, [node])

        const isManufacturerOrBODNode = useMemo(
            () => isManufacturerOrBODPill(node),
            [node]
        )
        const shouldRenderBPIcon = useShouldRenderBPIcon(node)
        const showBestPracticesIcons = useMemo(
            () =>
                shouldRenderBPIcon &&
                !isManufacturerOrBODNode &&
                !isProductLibraryButtonNode &&
                !isOMAddManufacturerNode &&
                showBestPracticesFeatureInNewWorkspace,
            [
                shouldRenderBPIcon,
                isManufacturerOrBODNode,
                isProductLibraryButtonNode,
                isOMAddManufacturerNode,
                showBestPracticesFeatureInNewWorkspace,
            ]
        )

        const handleClick = useCallback(
            (e: React.MouseEvent) => {
                setSelectedKey(node.key)

                const target = e.target as HTMLElement
                if (isEditableElement(target)) return

                spanRef.current?.focus()
            },
            [node.key, setSelectedKey]
        )

        // Best Practices right-click handler
        const handleRightClick = useCallback(
            (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                event.preventDefault()
                event.stopPropagation()

                // DataNode uses 'key' as the identifier, not 'id'
                const contentItemId = node.key

                // Only enable BP menu for articles and paragraphs (not for manufacturer/BOD nodes or product library buttons)
                const isManufacturerOrBODNodeLocal = isManufacturerOrBODPill(
                    node
                )
                const isProductLibraryButtonNodeLocal =
                    processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                        '1' &&
                    (String(contentItemId).startsWith('product-library-btn') ||
                        String(contentItemId).startsWith('om-add-mfr-btn'))

                if (
                    isManufacturerOrBODNodeLocal ||
                    isProductLibraryButtonNodeLocal ||
                    !contentItemId ||
                    !projectId ||
                    !showBestPracticesFeatureInNewWorkspace ||
                    !showBPSetting ||
                    shouldDisableBPMenuForUserAddedContent ||
                    isFromPPLTemplate ||
                    isCurrentlyOnOMWorkspace
                ) {
                    return
                }

                if (event.type === 'contextmenu') {
                    openBPMenu({
                        anchorElement: event.currentTarget,
                        contentItemId: contentItemId,
                        contentItemType: node.type || '',
                        projectId: projectId,
                        // No elementId for articles/paragraphs (only for pills)
                        elementId: undefined,
                    })
                }
            },
            [
                node,
                projectId,
                openBPMenu,
                showBestPracticesFeatureInNewWorkspace,
                shouldDisableBPMenuForUserAddedContent,
                showBPSetting,
                isFromPPLTemplate,
                isCurrentlyOnOMWorkspace,
            ]
        )

        const handleDoubleClick = useCallback(
            (event: React.MouseEvent<HTMLSpanElement>) => {
                if (!isBPManagementEnabled) return

                event.preventDefault()
                event.stopPropagation()

                const contentItemId = node.key
                const isManufacturerOrBODNode = isManufacturerOrBODPill(node)
                const isProductLibraryButtonNode =
                    processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                        '1' &&
                    contentItemId === BP_NODE_KEYS.PRODUCT_LIBRARY_BUTTON

                if (
                    isManufacturerOrBODNode ||
                    isProductLibraryButtonNode ||
                    !contentItemId ||
                    !projectId ||
                    !showBPSetting
                ) {
                    return
                }

                applyBPFromConfig({
                    anchorElement: event.currentTarget,
                    contentItemId: contentItemId,
                    contentItemType: node.type || '',
                    projectId: projectId,
                    elementId: undefined,
                })
            },
            [
                node,
                projectId,
                showBPSetting,
                applyBPFromConfig,
                isBPManagementEnabled,
            ]
        )

        const nodeContent = (
            <span
                ref={spanRef}
                className={clsx(
                    displayWrap,
                    node.type !== NODE_TYPES.Article
                        ? classes.nonArticleNodes
                        : '',
                    node.type !== NODE_TYPES.Article && showBestPracticesIcons
                        ? nonArticleNodesBPSpacing
                        : '',
                    'article-container',
                    showCustomManufacturer && customManufacturerNode,
                    isOMAddManufacturerNode ? addManufacturerButtonNode : '',
                    isPromoteDemoteInFlight && promoteDemoteInFlight,
                    isPreviewNode && isBPManagementEnabled
                        ? classes.previewHighlight
                        : ''
                )}
                onClick={handleClick}
                onKeyDown={onKeyDown}
                onContextMenu={handleRightClick}
                onDoubleClick={
                    isBPManagementEnabled ? handleDoubleClick : undefined
                }
                onMouseDown={
                    isBPManagementEnabled
                        ? preventDoubleClickWordSelect
                        : undefined
                }
            >
                {previewFeatures.hasLosAutomation &&
                    !isCurrentlyOnOMWorkspace &&
                    !isManufacturerOrBODNode &&
                    !isProductLibraryButtonNode &&
                    !isOMAddManufacturerNode && (
                        <LevelOfSpecificationIcon node={node} />
                    )}
                {hasTaskIndicator && (
                    <TaskIndicatorButton
                        onClick={handleTaskIndicatorClick}
                        style={{
                            position: 'absolute',
                            zIndex: 2,
                            // Articles: 4 hover icons → wrapper at -96px, indicator at -120px
                            // Paragraphs: 3 hover icons → wrapper at -72px, indicator shifts +24px to -96px
                            left: pxToRem(isArticleNode ? -120 : -96),
                        }}
                    />
                )}
                {showBestPracticesIcons && (
                    <BestPracticesIconWrapper
                        contentItem={node}
                        projectElementNodeId={selectedProjectElementNode?.id}
                    />
                )}
                {!isManufacturerOrBODNode &&
                    !isProductLibraryButtonNode &&
                    !isOMAddManufacturerNode && (
                        <TreeViewHoveredIcons
                            node={node}
                            classes={classes}
                            treeRef={treeRef}
                        />
                    )}
                {showNodeOrder &&
                    !isManufacturerOrBODNode &&
                    !isProductLibraryButtonNode &&
                    !isOMAddManufacturerNode && (
                        <NodeOrder
                            itemOrder={orderLabel}
                            classes={clsx(
                                isContentHidden && [inactiveText, 'node-order'],
                                hasSpecialContentTypes && nodeOrderPadding,
                                isArticleNode
                                    ? articleTypography
                                    : paragraphTypography
                            )}
                        />
                    )}
                {processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                    '1' &&
                    isProductLibraryButtonNode && (
                        <div className={productLibraryButtonContainer}>
                            {processEnv.REACT_APP_EnableAddCustomManufacturerInBetaWorkspace ===
                                '1' &&
                                node.parentContentItemId && (
                                    <CustomManufacturerFieldsWrapper
                                        selectedProjectElementNode={
                                            selectedProjectElementNode
                                        }
                                        isHidden={
                                            Boolean(node.parentIsHidden) &&
                                            (!isContentTeamUser ||
                                                isCurrentlyOnOMWorkspace) &&
                                            !shouldSuppressStrikethrough
                                        }
                                        parentElementId={
                                            node?.parentProductTypeId
                                        }
                                        rootArticleId={node?.rootArticleId}
                                    />
                                )}
                            {!isImportedFamily && (
                                <ProductLibraryButton
                                    tooltipText="View and Select Manufacturers and Products"
                                    iconFill="#314959"
                                    onClick={() =>
                                        handleViewProductsWithElementItem(
                                            selectedProjectElementNode as ProjectElementNodeExtended,
                                            elementItemValue,
                                            userId,
                                            projectId
                                        )
                                    }
                                    buttonClassName={
                                        classes.productLibraryButton
                                    }
                                />
                            )}
                        </div>
                    )}
                {/* Bug 2684545 / 2684633: AddManufacturer is suppressed for
                    Product Family selections — the button has no meaningful
                    target at that level. The transformer also skips emitting
                    the synthetic `om-add-mfr-btn-*` row in this case, so this
                    gate is defense-in-depth. */}
                {processEnv.REACT_APP_EnableDynamicManufacturerListSelection ===
                    '1' &&
                    processEnv.REACT_APP_EnableAddCustomManufacturerInBetaWorkspace ===
                        '1' &&
                    isOMAddManufacturerNode &&
                    !isProductFamilySelected && (
                        <AddManufacturerButton
                            selectedProjectElementNode={
                                selectedProjectElementNode
                            }
                            rootArticleId={node?.rootArticleId}
                        />
                    )}

                {!isProductLibraryButtonNode &&
                    !isOMAddManufacturerNode &&
                    (node.type === NODE_TYPES.Table ? (
                        <TableContent node={node} />
                    ) : (
                        <ContentRenderer
                            node={node}
                            inactive={isContentHidden}
                            editable={isNodeEditable}
                        />
                    ))}
            </span>
        )

        return (
            <>
                {hasParentComments &&
                    renderSpecifierNotes('specifier-notes', hasParentComments)}
                {!isArticleNode && renderSpecifierNotes('specifier-notes')}

                {isOMDeactivatedTooltipVisible ? (
                    <HoverTooltip
                        text={OM_DEACTIVATED_NODE_TOOLTIP}
                        placements={['top']}
                    >
                        {nodeContent}
                    </HoverTooltip>
                ) : (
                    nodeContent
                )}

                {/* Article specifier notes is rendered under node content */}
                {showArticleNote &&
                    renderSpecifierNotes('specifier-notes-smm-om')}

                {/* Best Practices Setting Popover */}
                {isBPMenuOpen && bpAnchorElement && (
                    <BestPracticesSettingPopover
                        anchorElement={bpAnchorElement}
                        isBestPractice={node.isBestPractice ?? false}
                        isGlobalBestPractice={
                            node.isGlobalBestPractice ?? false
                        }
                        isBestPracticeEditDisabled={false}
                        onCloseMainMenu={closeBPMenu}
                    />
                )}
            </>
        )
    }
)
